import React, { FC, useState, useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import QueryString from 'query-string'
import { useAuth0 } from '@auth0/auth0-react'

import { getCareteamAPI } from '@apiRoutes'
import { Layout, Loading, ErrorBanner } from '@components'
import { useAuthToken, navigateToUrl, routes } from '@utils'
import styles from './styles.module.scss'
import _model from './model.en.json'

type Localization = {
  serverError: string
}

const model = _model as Localization

const OnboardUser: FC<RouteComponentProps> = ({ history, location }) => {
  const { loginWithRedirect, user } = useAuth0()

  const { app, action } = QueryString.parse(location.search)

  const { authToken, tokenPending } = useAuthToken()
  const [{ patientFetching, patientFetchingComplete }, setPatient] = useState({
    patientFetching: false,
    patientFetchingComplete: false
  })

  const [screenError, setScreenError] = useState('' as string)

  const addScreenError = (errorText: string) => {
    setScreenError(`${errorText.charAt(0).toUpperCase()}${errorText.slice(1)}`)
  }

  useEffect(() => {
    if (tokenPending || patientFetching || patientFetchingComplete) {
      return
    }

    if (!authToken) {
      const redirectConfig = { initial_screen: '', app_origin: app }

      redirectConfig.initial_screen =
        action === 'signup' ? 'newaccount' : 'login'

      loginWithRedirect(redirectConfig)
      return
    }

    if (user) {
      if (!user?.email_verified) {
        navigateToUrl(history, routes.emailVerification)
        return
      }
    }

    const fetchData = async () => {
      setPatient({
        patientFetching: true,
        patientFetchingComplete: false
      })

      const response = await getCareteamAPI(authToken || '')
      if (!response.success) {
        addScreenError(model.serverError)
        return
      }

      const ptt = response.responsePayload.careTeams[0]?.patient
      const treatmentCode =
        (app as string) || (process.env.REACT_APP_TO1_PRODUCT_CODE as string)
      const mostRecentTreatment = ptt.treatments?.[treatmentCode]?.[0]

      setPatient({
        patientFetching: false,
        patientFetchingComplete: true
      })

      if (mostRecentTreatment?.isActive) {
        navigateToUrl(history, routes.downloadT01App)
        return
      }

      // when: !careteam || careteam but !patient || careteam patient but !active treatment
      navigateToUrl(history, routes.setupCaregiverProfile)
    }
    fetchData()
  }, [
    authToken,
    tokenPending,
    app,
    action,
    history,
    loginWithRedirect,
    user,
    patientFetching,
    patientFetchingComplete
  ])

  /** ************* */
  const renderErrorBanner = () => {
    return (
      <div className={styles.errorContainer}>
        <ErrorBanner message={screenError} />
      </div>
    )
  }

  return (
    <Layout>
      <div className={styles.container}>
        {screenError ? renderErrorBanner() : <Loading fullScreen />}
      </div>
    </Layout>
  )
}

export default OnboardUser
